import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { IGameInfoDto } from 'modules/overlay-v2/domain/interfaces/game-info/game-info-dto';
import {
  Championship,
  GameInfoMapped,
  Match,
  Member,
  Organizer,
  Team,
  Teams,
} from 'modules/overlay-v2/domain/interfaces/game-info/gameInfoMapped';
import { KitElement } from 'shared/kit/kit-element';
import { KitPattern } from 'shared/kit/kit-pattern';
import { KitType } from 'shared/kit/kit-type';
import { TeamKit } from 'shared/kit/team-kit';
import { getCityFromAddress } from 'shared/utils';

// Игроки без указанной позиции по умолчанию считаются полузащитниками,
// иначе карусель составов (которая перебирает фиксированный набор позиций) их пропустит.
const DEFAULT_POSITION = 'midfielder';
const DEFAULT_NUMBER = 0;

export function gameInfoDataMapper(gameInfo: IGameInfoDto): GameInfoMapped {
  return {
    match: extractMatch(gameInfo),
    teams: extractTeams(gameInfo),
    organizer: extractOrganizer(gameInfo),
    championship: extractChampionship(gameInfo),
    sportType: extractSportType(gameInfo),
  };
}

// GraphQL отдаёт вид спорта заглавными (`FOOTBALL`), а enum в домене — строчными.
function extractSportType(gameInfo: IGameInfoDto): SportType | undefined {
  const sportType = gameInfo.sportType?.toLowerCase();
  return sportType ? (sportType as SportType) : undefined;
}

function extractMatch(gameInfo: IGameInfoDto): Match {
  const date = gameInfo?.date ? new Date(gameInfo?.date) : undefined;
  const location = gameInfo?.location;
  const tournament = gameInfo?.tournament;

  return {
    id: gameInfo.id,
    dateInfo: {
      fullDate: gameInfo.date,
      date: date ? format(date, 'd MMMM', { locale: ru }) : 'Неизвестная дата',
      time: date ? format(date, 'HH:mm') : 'Неизвестное время',
    },
    address: {
      city: getCityFromAddress(location?.address) ?? getCityFromAddress(tournament?.address) ?? 'Неизвестный город',
      country: location?.address?.country,
      region: location?.address?.region,
    },
  };
}

function extractTeams(gameInfo: IGameInfoDto): Teams {
  return {
    firstTeam: extractTeam(gameInfo, 0),
    secondTeam: extractTeam(gameInfo, 1),
  };
}

function extractTeam(gameInfo: IGameInfoDto, teamIndex: number): Team | undefined {
  if (!gameInfo.teamMatches) return undefined;

  const id = gameInfo.teamMatches[teamIndex].team.id;
  const lastGameResult = gameInfo.championship?.championshipTable.rows.find((row) => row.team.id === id)?.lastGamesResults;

  return {
    id: gameInfo.teamMatches[teamIndex]?.team?.id,
    name: gameInfo.teamMatches[teamIndex]?.team?.name,
    shortName: gameInfo.teamMatches[teamIndex]?.team?.shortName,
    logo: gameInfo.teamMatches[teamIndex]?.team?.emblem?.externalUrl,
    lastGamesResults: lastGameResult ?? [],
    kits: extractKits(gameInfo, teamIndex),
    members: extractMembers(gameInfo, teamIndex),
    score: 0,
    fouls: 0,
  };
}

// GraphQL отдаёт тип комплекта, элемент и узор заглавными (`HOME`, `SHIRT`, `SOLID`),
// а enum-ы в `shared/kit` — строчными; `resolveKitOutfit` сравнивает их строго.
function extractKits(gameInfo: IGameInfoDto, teamIndex: number): TeamKit[] {
  const kits = gameInfo.teamMatches?.[teamIndex]?.team?.kits;
  if (!kits) return [];

  return kits.map((kit) => ({
    id: kit.id,
    type: kit.type.toLowerCase() as KitType,
    elements: (kit.elements ?? []).map((el) => ({
      element: el.element.toLowerCase() as KitElement,
      attributes: {
        pattern: el.attributes.pattern.toLowerCase() as KitPattern,
        colors: el.attributes.colors ?? [],
      },
    })),
  }));
}

function extractMembers(gameInfo: IGameInfoDto, teamIndex: number): Member[] {
  if (!gameInfo.teamMatches) return [];

  const championshipId = gameInfo.championship?.id;
  const members = gameInfo.teamMatches[teamIndex].team.championshipApplications;
  const filteredMembers: Member[] = [];

  for (const member of members) {
    if (member.championship.id !== championshipId) continue;
    if (member.state !== 'ACCEPTED') continue;
    filteredMembers.push({
      id: member.user.id,
      name: member.user.name,
      surname: member.user.surname,
      middleName: member.user.middleName ?? '',
      photo: getPhoto(gameInfo, member.user.id, teamIndex),
      position: member.position || DEFAULT_POSITION,
      number: member.number || DEFAULT_NUMBER,
      isInvolved: isInvolved(gameInfo, member.user.id),
      qtyGoalsMatch: 0,
      qtyYellowCardsMatch: 0,
      qtyRedCardsMatch: 0,
      qtyFoulsMatch: 0,
      qtyShootoutGoalsMatch: 0,
      qtyShootoutMissesMatch: 0,
    });
  }

  return filteredMembers;
}

function isInvolved(gameInfo: IGameInfoDto, memberId: number) {
  const member = gameInfo.participants?.find((participant) => participant.user.id === memberId);
  if (!member) return false;
  return member.isInvolved;
}

// Возвращаем undefined, а не дефолтный аватар: потребители сами решают, чем заменить
// отсутствующее фото (карусель составов подставляет логотип команды, остальные — дефолтный аватар).
function getPhoto(gameInfo: IGameInfoDto, memberId: number, teamIndex: number): string | undefined {
  if (!gameInfo.teamMatches) return undefined;
  const member = gameInfo.teamMatches[teamIndex].team.members.find((member) => member.user.id === memberId);
  if (!member) return undefined;
  return member.user.photo?.url;
}

function extractOrganizer(gameInfo: IGameInfoDto): Organizer | undefined {
  if (!gameInfo.tournament) return undefined;

  return {
    name: gameInfo.tournament?.name ?? 'Неизвестно',
    logo: gameInfo.tournament?.images?.[0]?.externalUrl,
    id: gameInfo.tournament?.id,
  };
}

function extractChampionship(gameInfo: IGameInfoDto): Championship | undefined {
  if (!gameInfo.championship) return undefined;

  const overlayIdStr = gameInfo.championship.settings.find((setting) => setting.name === 'overlay_id')?.value;

  return {
    id: gameInfo.championship.id,
    name: gameInfo.championship.name,
    settings: gameInfo.championship.settings,
    overlayId: overlayIdStr ? +overlayIdStr : null,
  };
}
